import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
    IconButton, Button, Checkbox, Chip, Zoom, Stack, Grid, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import { GenericMessageDialog } from '../Common/Components/GenericMessageDialog';
import { httpGet } from '../../HttpRequestHandling/httpRequestHandler';
import { toast } from 'react-toastify';
import { formatNumber, getDisplayName, stringIsNullOrEmpty } from '../Common/Utilities';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Zoom in={true} ref={ref} {...props} />;
});

export function PlacementLevelLookupDialog(props) {
    const { info, onClose } = props;

    // --- State ---
    const [openConfirm, setOpenConfirm] = useState(false);
    const [lockedIOs, setLockedIOs] = useState(new Set());
    const [ioData, setIoData] = useState([]);
    const [selectedIOs, setSelectedIOs] = useState([]);
    const [loadingIOs, setLoadingIOs] = useState(new Set());
    
    const getInitialMode = () => {
        const existingLines = info.existingDetailLines || [];
        const selectedSeqIds = info.selectedRow ? info.selectedRow.map(r => r.seqId) : [];
        
        // 1. Check selected rows first
        for (const line of existingLines) {
            if (selectedSeqIds.includes(line.seqId) && line.ioSessionMode) {
                return line.ioSessionMode;
            }
        }
        
        // 2. Fallback: Top-down search for the first assigned mode
        for (const line of existingLines) {
            if (line.ioSessionMode) {
                return line.ioSessionMode;
            }
        }
        
        // 3. Fallback to the overall document session mode, then prop, or null
        return info.ioSessionMode || info.documentSessionMode || info.placementSessionMode || null;
    };

    const initialSessionMode = getInitialMode();
        
    const [sessionMode, setSessionMode] = useState(initialSessionMode);
    const [placementsCache, setPlacementsCache] = useState({});
    const [showAllPlacements, setShowAllPlacements] = useState(false);
    const ioGridRef = useRef(null);
    const [includeAllPO, setIncludeAllPO] = useState(
        info.serviceDateFilterForIoLookup === "OPTIONAL_OVERRIDE" &&
            stringIsNullOrEmpty(info.serviceTermStartDate) &&
            stringIsNullOrEmpty(info.serviceTermEndDate)
    );

    const getOverallMode = () => {
        const existingLines = info.existingDetailLines || [];
        const selectedSeqIds = info.selectedRow ? info.selectedRow.map(r => r.seqId) : [];
        let mode = null;
        for (const line of existingLines) {
            if (!selectedSeqIds.includes(line.seqId) && line.ioSessionMode) {
                if (!mode) mode = line.ioSessionMode;
                else if (mode !== line.ioSessionMode) return 'mixed';
            }
        }
        return mode;
    };
    const overallMode = getOverallMode();

    const fDate = (dStr) => {
        if (!dStr) return '';
        const d = new Date(dStr);
        if (isNaN(d.getTime())) return dStr;
        return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    };

    // --- 1. Initialization Logic ---
    useEffect(() => {
        if (info.openDialog) {
            const initialIncludeAllPO = info.serviceDateFilterForIoLookup === "OPTIONAL_OVERRIDE" &&
                stringIsNullOrEmpty(info.serviceTermStartDate) &&
                stringIsNullOrEmpty(info.serviceTermEndDate);
            setIncludeAllPO(initialIncludeAllPO);
            initData(initialIncludeAllPO);
        }
    }, [info.openDialog]);

    const getOtherLinesAllocated = (ioId, placementId) => {
        const existingLines = info.existingDetailLines || [];
        const selectedSeqIds = info.selectedRow ? info.selectedRow.map(r => r.seqId) : [];
        let total = 0;
        for (const line of existingLines) {
            if (selectedSeqIds.includes(line.seqId)) continue;
            if (line.ioNumber === ioId) {
                if (line.placements) {
                    const match = line.placements.find(p => p.placementId === placementId);
                    if (match) total += (parseFloat(match.allocAmount) || 0);
                }
            }
        }
        return total;
    };

    const buildIOListUrl = (includeAllOverride) => {
        const effectiveIncludeAllPO = includeAllOverride !== undefined ? includeAllOverride : includeAllPO;
        let serviceDateFilterApplicable = 1;
        if (info.serviceDateFilterForIoLookup === "NOT_APPLICABLE" || info.modelDefId !== 1) {
            serviceDateFilterApplicable = 0;
        } else if (info.serviceDateFilterForIoLookup === "OPTIONAL_OVERRIDE") {
            serviceDateFilterApplicable = effectiveIncludeAllPO ? 0 : 1;
        }

        let url = `/api/lookup/poheaderlistbyvendor/${encodeURIComponent(info.vendorCode)}/${info.modelDefId}/${info.invoiceDocumentId || 0}/${info.clientCode || null}`;
        if (info.serviceTermStartDate && info.serviceTermEndDate) url += `/${info.serviceTermStartDate}/${info.serviceTermEndDate}`;
        url += `?serviceDateFilterApplicable=${serviceDateFilterApplicable}`;
        return url;
    };

    const initData = async (includeAllOverride) => {
        if (info.inProgress) info.inProgress(true);

        const url = buildIOListUrl(includeAllOverride);

        try {
            const ioResponse = await httpGet(url);
            const allIOs = ioResponse.data || [];
            setIoData(allIOs);

            const existingLines = info.existingDetailLines || [];
            const hydratedSelections = [];
            const currentlyLocked = new Set();
            const selectedSeqIds = info.selectedRow ? info.selectedRow.map(r => r.seqId) : [];

            // Target lines only (for hydrating dialog state)
            const targetLines = existingLines.filter(line => selectedSeqIds.includes(line.seqId));

            for (const line of targetLines) {
                if (line.ioNumber) {
                    if (line.lineStatus >= 70 && line.lineStatus !== 80) {
                        currentlyLocked.add(line.ioNumber);
                    }

                    const matchedIO = info.poHeaderLookup?.find(io => io.poCode === line.ioNumber) || allIOs.find(i => i.poCode === line.ioNumber);
                    if (matchedIO) {
                        const pls = await fetchPlacementsApi(line.ioNumber);
                        
                        // Map existing line placements to cache
                        const linePls = line.placements || [];
                        const mappedPlacements = pls.map(p => {
                            const otherAlloc = getOtherLinesAllocated(line.ioNumber, p.placementId);
                            const plRem = (p.amount - (p.consumedAmt || 0)) - otherAlloc;
                            const match = linePls.find(lp => lp.placementId === p.placementId);
                            if (match || line.ioSessionMode === 'io' || line.placementSessionMode === 'io') {
                                return { ...p, checked: true, allocAmount: (match && match.allocAmount !== undefined) ? match.allocAmount : plRem };
                            }
                            return { ...p, checked: false, allocAmount: 0 };
                        }).filter(p => p.checked);

                        hydratedSelections.push({
                            ...matchedIO,
                            mode: info.ioSessionMode || info.placementSessionMode || 'io',
                            placements: mappedPlacements,
                            allocAmount: mappedPlacements.reduce((s, p) => s + (p.allocAmount || 0), 0)
                        });
                    }
                }
            }
            setLockedIOs(currentlyLocked);
            setSelectedIOs(hydratedSelections);
        } finally {
            if (info.inProgress) info.inProgress(false);
        }
    };

    const fetchPlacementsApi = async (ioId) => {
        if (placementsCache[ioId]) return placementsCache[ioId];
        const res = await httpGet(`/api/lookup/placements/${encodeURIComponent(ioId)}/${info.modelDefId}/${info.invoiceDocumentId || 0}`);
        const data = res.data || [];
        setPlacementsCache(prev => ({ ...prev, [ioId]: data }));
        return data;
    };

    // --- 2. Action Handlers ---
    const handleCheckboxToggle = async (row, event) => {
        event.stopPropagation();
        if (sessionMode === 'placement' || lockedIOs.has(row.poCode) || loadingIOs.has(row.poCode)) return;

        const isSelected = selectedIOs.some(s => s.poCode === row.poCode);
        if (isSelected) {
            const next = selectedIOs.filter(s => s.poCode !== row.poCode);
            setSelectedIOs(next);
            if (next.length === 0) setSessionMode(null);
        } else {
            if (!sessionMode) setSessionMode('io');
            setLoadingIOs(prev => new Set(prev).add(row.poCode));
            try {
                const pls = await fetchPlacementsApi(row.poCode);
                const mappedPlacements = pls.map(p => {
                    const otherAlloc = getOtherLinesAllocated(row.poCode, p.placementId);
                    const plRem = (p.amount - (p.consumedAmt || 0)) - otherAlloc;
                    return { ...p, checked: true, allocAmount: plRem };
                });
                const newIO = { ...row, mode: 'io', placements: mappedPlacements, allocAmount: mappedPlacements.reduce((s, p) => s + p.allocAmount, 0) };
                setSelectedIOs(prev => info.allowMultipleSelection ? [...prev, newIO] : [newIO]);
            } finally {
                setLoadingIOs(prev => {
                    const next = new Set(prev);
                    next.delete(row.poCode);
                    return next;
                });
            }
        }
    };

    const handleRowClick = async (params) => {
        const row = params.row;
        if (sessionMode === 'io' || lockedIOs.has(row.poCode) || loadingIOs.has(row.poCode)) return;
        
        if (!sessionMode) setSessionMode('placement');
        setLoadingIOs(prev => new Set(prev).add(row.poCode));
        try {
            await fetchPlacementsApi(row.poCode);
            if (!selectedIOs.some(s => s.poCode === row.poCode)) {
                const newIO = { ...row, mode: 'placement', placements: [], allocAmount: 0 };
                setSelectedIOs(prev => info.allowMultipleSelection ? [...prev, newIO] : [newIO]);
            }
        } finally {
            setLoadingIOs(prev => {
                const next = new Set(prev);
                next.delete(row.poCode);
                return next;
            });
        }
    };

    const togglePlacement = (ioId, placement, checked) => {
        setSelectedIOs(prev => prev.map(io => {
            if (io.poCode !== ioId) return io;
            let updated;
            if (checked) {
                const otherAlloc = getOtherLinesAllocated(ioId, placement.placementId);
                const plRem = (placement.amount - (placement.consumedAmt || 0)) - otherAlloc;
                updated = [...io.placements, { ...placement, checked: true, allocAmount: plRem }];
            } else {
                updated = io.placements.filter(p => p.placementId !== placement.placementId);
            }
            return { ...io, placements: updated, allocAmount: updated.reduce((s, p) => s + (p.allocAmount || 0), 0) };
        }));
    };
    
    const toggleAllPlacements = (ioId, checked) => {
        const pls = placementsCache[ioId] || [];
        setSelectedIOs(prev => prev.map(io => {
            if (io.poCode !== ioId) return io;
            const updated = checked ? pls
                .filter(p => (p.amount - (p.consumedAmt || 0)) > 0)
                .map(p => {
                    const otherAlloc = getOtherLinesAllocated(ioId, p.placementId);
                    const plRem = (p.amount - (p.consumedAmt || 0)) - otherAlloc;
                    return { ...p, checked: true, allocAmount: plRem };
                }) : [];
            return { ...io, placements: updated, allocAmount: updated.reduce((s, p) => s + (p.allocAmount || 0), 0) };
        }));
    };

    const handleallocAmountChange = (ioId, placementId, value) => {
        const numericValue = parseFloat(value) || 0;
        setSelectedIOs(prev => prev.map(io => {
            if (io.poCode !== ioId) return io;
            const updated = io.placements.map(p => {
                if (p.placementId === placementId) {
                    return { ...p, allocAmount: numericValue };
                }
                return p;
            });
            return { ...io, placements: updated, allocAmount: updated.reduce((s, p) => s + (p.allocAmount || 0), 0) };
        }));
    };

    const handleReset = () => {
        setSelectedIOs([]);
        setSessionMode(null);
        setLockedIOs(new Set());
    };
    
    const removeIO = (ioId) => {
        const next = selectedIOs.filter(s => s.poCode !== ioId);
        setSelectedIOs(next);
        if (next.length === 0) setSessionMode(null);
    };

    const handleRefresh = () => {
        const filterText = ioGridRef?.current?.filterText;
        if (stringIsNullOrEmpty(filterText)) return;
        if (info.inProgress) info.inProgress(true);
        httpGet(`/api/invoicedocument/refetchpoinfo/${info.modelDefId}/${filterText}`)
            .then(() => {
                setIoData([]);
                initData();
                info.inProgress(false);
            })
            .catch(() => {
                info.inProgress(false);
                toast.error('Error fetching IO/PO Number, either the IO/PO number is invalid or the record does not exists.');
            });
    };

    const applyIncludeAllPOChange = async (isChecked) => {
        setIncludeAllPO(isChecked);

        if (info.inProgress) info.inProgress(true);
        try {
            const url = buildIOListUrl(isChecked);
            const ioResponse = await httpGet(url);
            setIoData(ioResponse.data || []);
        } finally {
            if (info.inProgress) info.inProgress(false);
        }
    };

    const handleIncludeAllIOChange = (event) => {
        const isChecked = event.target.checked;
        if (includeAllPO === isChecked) return;

        // Unchecking re-applies the strict service date filter, which can strand IOs/placements
        // already selected in this session, so confirm with the user before resetting them.
        if (!isChecked && selectedIOs.length > 0) {
            setOpenConfirm(true);
            return;
        }

        applyIncludeAllPOChange(isChecked);
    };

    const confirmDisableIncludeAllPO = () => {
        setOpenConfirm(false);
        setSelectedIOs([]);
        setSessionMode(null);
        setLockedIOs(new Set());
        applyIncludeAllPOChange(false);
    };

    const cancelDisableIncludeAllPO = () => {
        setOpenConfirm(false);
    };

    const getIncludeAllIO = () => {
        if (info.serviceDateFilterForIoLookup !== "OPTIONAL_OVERRIDE") return null;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 'normal', pr: '10px', pl: '3px', whiteSpace: 'nowrap' }}>
                <Checkbox size="small" checked={includeAllPO} onChange={handleIncludeAllIOChange} />
                <Typography sx={{ fontSize: '12px', color: '#374151', whiteSpace: 'nowrap' }}>Include All IO/PO</Typography>
            </Box>
        );
    };

    // --- 3. Grid Definition (Left Pane) ---
    const columns = [
        {
            headerName: ' ', field: 'chk', width: 45, renderCell: p => (
                loadingIOs.has(p.row.poCode) ? (
                    <CircularProgress size={20} />
                ) : (
                    <Checkbox size="small" checked={selectedIOs.some(s => s.poCode === p.row.poCode && s.mode === 'io')}
                        indeterminate={selectedIOs.some(s => s.poCode === p.row.poCode && s.mode === 'placement')}
                        disabled={sessionMode === 'placement' || lockedIOs.has(p.row.poCode)} onClick={(e) => handleCheckboxToggle(p.row, e)} />
                )
            )
        },
        { headerName: 'IO/PO Number', field: 'poCode', width: 150, renderCell: p => (
            <Box><Typography variant="body2" sx={{ color: '#2563eb', fontWeight: 500 }}>{p.row.poCode}</Typography></Box>
        ) },
        { headerName: getDisplayName('site'), field: 'siteName', width: 120 },
        { headerName: 'Service Date', field: 'serviceStartDate', width: 150, renderCell: p => p.row.serviceStartDate || '' },
        { headerName: 'IO/PO Amount', field: 'poAmount', align: 'right', renderCell: p => formatNumber(p.row.poAmount), width: 100 },
        { headerName: 'Consumed Amount', field: 'consumedAmt', align: 'right', renderCell: p => formatNumber(p.row.consumedAmount), width: 100 },
        { headerName: 'Remaining Amount', field: 'rem', align: 'right', renderCell: p => formatNumber(p.row.poAmount - p.row.consumedAmount), width: 100 },
        { headerName: 'Delivery Amount', field: 'deliveryAmount', align: 'right', renderCell: p => formatNumber(p.row.deliveryAmount), width: 100 },
        { headerName: 'Currency', field: 'currencyCode', width: 90 },
        { headerName: 'Client Name', field: 'clientName', width: 140 },
        { headerName: 'Job Code', field: 'jobCode', width: 100 },
        { headerName: 'Campaign Name', field: 'campaignName', width: 150 },
        { headerName: 'Media Plan Name', field: 'mediaPlanName', width: 150 }
    ];

    const grandTotalAllocated = selectedIOs.reduce((sum, io) => sum + (io.allocAmount || 0), 0);

    const isValidationFailed = React.useMemo(() => {
        if (selectedIOs.length === 0) return true;
        if (sessionMode === 'io') {
            return selectedIOs.some(io => {
                const ioTolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (io.poAmount || 0)) / 100;
                const ioMaxAllowedBudget = (io.poAmount || 0) + ioTolerance;
                return (ioMaxAllowedBudget - (io.consumedAmount || 0)) <= 0;
            });
        }
        if (sessionMode === 'placement') {
            if (grandTotalAllocated <= 0) return true;
            
            for (const io of selectedIOs) {
                // Must allocate budget for selected IO
                if ((io.allocAmount || 0) <= 0) return true;
                
                // Cannot exceed IO budget
                const ioTolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (io.poAmount || 0)) / 100;
                const ioMaxAllowedBudget = (io.poAmount || 0) + ioTolerance;
                const ioTotalRequestedAmount = (io.consumedAmount || 0) + (io.allocAmount || 0);
                if (ioTotalRequestedAmount > ioMaxAllowedBudget) return true;
                
                // Check individual placements ONLY when the IO is in placement mode
                if (io.mode === 'placement') {
                    for (const p of io.placements) {
                        const otherAlloc = getOtherLinesAllocated(io.poCode, p.placementId);
                        const tolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (p.amount || 0)) / 100;
                        const maxAllowedBudget = (p.amount || 0) + tolerance;
                        const totalRequestedAmount = (p.consumedAmt || 0) + otherAlloc + (p.allocAmount || 0);

                        if ((p.allocAmount || 0) <= 0) return true;
                        if (totalRequestedAmount > maxAllowedBudget) return true;
                    }
                }
            }
        }
        return false;
    }, [selectedIOs, sessionMode, grandTotalAllocated]);

    const getTooltipMessage = () => {
        if (selectedIOs.length === 0) return "Please select at least one item";
        if (sessionMode === 'io' && selectedIOs.some(io => {
            const ioTolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (io.poAmount || 0)) / 100;
            const ioMaxAllowedBudget = (io.poAmount || 0) + ioTolerance;
            return (ioMaxAllowedBudget - (io.consumedAmount || 0)) <= 0;
        })) {
            return "Cannot select fully consumed IO";
        }
        if (sessionMode === 'placement') {
            if (grandTotalAllocated <= 0) return "Total placement allocation must be greater than zero";
            
            for (const io of selectedIOs) {
                if ((io.allocAmount || 0) <= 0) return "All selected IOs must have an allocated budget greater than zero";
                const ioTolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (io.poAmount || 0)) / 100;
                const ioMaxAllowedBudget = (io.poAmount || 0) + ioTolerance;
                const ioTotalRequestedAmount = (io.consumedAmount || 0) + (io.allocAmount || 0);
                if (ioTotalRequestedAmount > ioMaxAllowedBudget) {
                    const ioRemWithTolerance = ioMaxAllowedBudget - (io.consumedAmount || 0);
                    return `Allocated amount cannot exceed the IO budget (${formatNumber(ioRemWithTolerance)})`;
                }
                
                if (io.mode === 'placement') {
                    for (const p of io.placements) {
                        const otherAlloc = getOtherLinesAllocated(io.poCode, p.placementId);
                        const tolerance = io.tolerancePOFlag === 'N' ? (io.tolerancePOAmount || 0) : ((io.tolerancePOAmount || 0) * (p.amount || 0)) / 100;
                        const maxAllowedBudget = (p.amount || 0) + tolerance;
                        const totalRequestedAmount = (p.consumedAmt || 0) + otherAlloc + (p.allocAmount || 0);

                        if ((p.allocAmount || 0) <= 0) return "Placement allocation must be greater than zero";
                        if (totalRequestedAmount > maxAllowedBudget) {
                            const plRemWithTolerance = maxAllowedBudget - (p.consumedAmt || 0) - otherAlloc;
                            return `Allocated amount cannot exceed placement budget (${formatNumber(plRemWithTolerance)})`;
                        }
                    }
                }
            }
        }
        return "";
    };

    return (
        <>
        <Dialog open={true} TransitionComponent={Transition} fullWidth maxWidth="xl" onClose={() => onClose(null)} PaperProps={{ sx: { height: '90vh' } }}>
            <DialogTitle sx={{ p: '12px 8px 12px 22px', borderBottom: '1px solid #e8eaed', bgcolor: '#fff' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, display: 'inline-block', mr: 2 }}>Select IO/PO Number</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6b7280', display: 'inline-block' }}>Vendor: {info.vendorCode} : {info.vendorName || ''}</Typography>
                    </Box>
                    <IconButton onClick={() => onClose(null)} size="small" sx={{ color: '#9ca3af', '&:hover': { bgcolor: '#f3f4f6' } }}><CloseIcon /></IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', bgcolor: '#f0f2f5', overflow: 'hidden' }}>
                <Grid container sx={{ flex: 1, overflow: 'hidden' }}>
                    {/* LEFT PANE (4) */}
                    <Grid item xs={selectedIOs.length > 0 ? 4 : 12} sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRight: '1px solid #e8eaed', height: '100%' }}>
                        
                        {/* Banner */}
                        <Box sx={{ display: sessionMode ? 'flex' : 'none', justifyContent: 'space-between', alignItems: 'center', p: '7px 14px', fontSize: '11px', borderBottom: '1px solid', bgcolor: sessionMode === 'io' ? '#eff6ff' : '#fffbeb', borderColor: sessionMode === 'io' ? '#bfdbfe' : '#fcd34d', color: sessionMode === 'io' ? '#1d4ed8' : '#92400e' }}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                <Typography variant="caption" sx={{ fontSize: '11px' }}>
                                    {sessionMode === 'io' 
                                        ? <React.Fragment><strong>Invoice by IO mode</strong> — checking the IO box submits all placements as one transaction. To invoice by placement instead, clear all and click a row.</React.Fragment>
                                        : <React.Fragment><strong>Invoice by Placement mode</strong> — select individual placements per IO. To invoice by IO instead, clear all and check an IO box.</React.Fragment>
                                    }
                                </Typography>
                            </Stack>
                            <Button size="small" variant="outlined" color="inherit" sx={{ fontSize: '10px', p: '2px 8px', minWidth: 0, textTransform: 'none', borderColor: 'currentColor' }} onClick={handleReset}>Clear all &amp; reset</Button>
                        </Box>
                            {overallMode && overallMode !== sessionMode && sessionMode && (
                                <Box sx={{ p: '4px 14px 7px 14px', color: '#d97706', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 600 }}>
                                        (Warning: Your selection mode '{sessionMode}' differs from other lines ({overallMode === 'mixed' ? 'which have mixed modes' : `which use '${overallMode}' mode`}))
                                    </Typography>
                                </Box>
                            )}

                        {/* Toolbar / Legend */}
                        <Box sx={{ p: '9px 14px 9px 22px', borderBottom: '1px solid #e8eaed', bgcolor: '#f8f9fb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Stack direction="row" gap={2}>
                                <Stack direction="row" alignItems="center" gap={0.5}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: '#2563eb' }} />
                                    <Typography sx={{ fontSize: '11px', color: '#6b7280' }}><strong>Check the box</strong> — invoice entire IO</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" gap={0.5}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: '#f59e0b' }} />
                                    <Typography sx={{ fontSize: '11px', color: '#6b7280' }}><strong>Click the row</strong> — select individual placements</Typography>
                                </Stack>
                            </Stack>
                        </Box>

                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <EnhancedDataGrid rows={ioData} columns={columns} getRowId={r => r.poCode} onRowClick={handleRowClick} disableSelectionOnClick hideFooter
                                gridRef={ioGridRef}
                                leftCustomComponent={getIncludeAllIO()}
                                customcomponent={
                                    <Tooltip title="Sync IO/PO from Nexelus">
                                        <IconButton size="small" onClick={handleRefresh} sx={{ float: 'right', zIndex: 1, marginTop: '10px' }}>
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                }
                                sx={{ '& .MuiDataGrid-row': { cursor: sessionMode === 'io' ? 'not-allowed' : 'pointer' } }} />
                        </Box>
                    </Grid>

                    {/* RIGHT PANE (8) */}
                    {selectedIOs.length > 0 && (
                        <Grid item xs={8} sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fb', height: '100%', overflow: 'hidden' }}>
                            <Box sx={{ p: '10px 16px', borderBottom: '1px solid #e8eaed', bgcolor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1a1a2e' }}>Placements</Typography>
                                    <Chip size="small" label={`${selectedIOs.length} IO${selectedIOs.length !== 1 ? 's' : ''}`} sx={{ fontSize: '10px', fontWeight: 600, bgcolor: '#2563eb', color: '#fff', height: 20 }} />
                                </Stack>
                                <Stack direction="row" alignItems="center" gap={0.5}>
                                        <Checkbox size="small" checked={showAllPlacements} onChange={e => setShowAllPlacements(e.target.checked)} sx={{ p: '2px' }} />
                                        <Typography sx={{ fontSize: '11px', color: '#6b7280', userSelect: 'none', cursor: 'pointer' }} onClick={() => setShowAllPlacements(prev => !prev)}>Show All</Typography>
                                    </Stack>
                            </Box>

                            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {selectedIOs.map(io => {
                                    const allPlacementsForIO = placementsCache[io.poCode] || [];
                                    const ioPlacementsList = showAllPlacements
                                        ? allPlacementsForIO
                                        : allPlacementsForIO.filter(p => (p.amount - (p.consumedAmt || 0)) > 0);
                                    const selectablePlacements = ioPlacementsList.filter(p => (p.amount - (p.consumedAmt || 0)) > 0);
                                    const budgetPct = io.poAmount > 0 ? Math.min(((io.allocAmount || 0) / io.poAmount) * 100, 100) : 0;
                                    const isOverBudget = (io.allocAmount || 0) > io.poAmount;
                                    
                                    const checkedCount = io.placements.length;
                                    let selStateText = io.mode === 'io' ? "All placements — invoiced by IO" : 
                                                        checkedCount === 0 ? "No placements selected" :
                                                        checkedCount === selectablePlacements.length ? `All ${selectablePlacements.length} placements selected` :
                                                        `${checkedCount} of ${selectablePlacements.length} placements selected`;

                                    const selStateBg = io.mode === 'io' ? '#ecfdf5' : (checkedCount === 0 ? '#f3f4f6' : (checkedCount < selectablePlacements.length ? '#fffbeb' : '#ecfdf5'));
                                    const selStateColor = io.mode === 'io' ? '#047857' : (checkedCount === 0 ? '#6b7280' : (checkedCount < selectablePlacements.length ? '#92400e' : '#047857'));
                                    const selStateBorder = io.mode === 'io' ? '#a7f3d0' : (checkedCount === 0 ? '#e5e7eb' : (checkedCount < selectablePlacements.length ? '#fcd34d' : '#a7f3d0'));

                                    const isIOLocked = lockedIOs.has(io.poCode);
                                    
                                    return (
                                        <Paper key={io.poCode} variant="outlined" sx={{ flexShrink: 0, borderRadius: '8px', overflow: 'hidden', borderTop: `3px solid ${io.mode === 'io' ? '#2563eb' : '#f59e0b'}` }}>
                                            
                                            {/* Block Header */}
                                            <Box sx={{ p: '10px 14px', borderBottom: '1px solid #e8eaed', bgcolor: '#fff' }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                                    <Stack direction="row" gap={1} alignItems="center">
                                                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#1a1a2e' }}>{io.poCode}</Typography>
                                                        <Box sx={{ fontSize: '10px', fontWeight: 500, borderRadius: '20px', px: '9px', py: '2px', border: '1px solid', bgcolor: selStateBg, color: selStateColor, borderColor: selStateBorder }}>
                                                            {selStateText}
                                                        </Box>
                                                    </Stack>
                                                    <Button size="small" variant="outlined" sx={{ color: '#9ca3af', borderColor: '#e8eaed', fontSize: '11px', textTransform: 'none', py: 0.5, '&:hover': { bgcolor: '#fef2f2', color: '#ef4444', borderColor: '#fca5a5' } }} onClick={() => removeIO(io.poCode)}>
                                                        ✕ Remove
                                                    </Button>
                                                </Stack>
                                                
                                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                    {[
                                                        { label: 'Client', val: io.clientName },
                                                        { label: 'Job Code', val: io.jobCode },
                                                        { label: 'Campaign', val: io.campaignName },
                                                        { label: 'Media Plan', val: io.mediaPlanName },
                                                        { label: 'IO Budget', val: formatNumber(io.poAmount) }
                                                    ].map((meta, idx) => (
                                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f8f9fb', border: '1px solid #e8eaed', borderRadius: '5px', overflow: 'hidden' }}>
                                                            <Typography sx={{ px: 1, py: 0.5, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', color: '#9ca3af', bgcolor: '#f0f2f5', borderRight: '1px solid #e8eaed' }}>{meta.label}</Typography>
                                                            <Typography sx={{ px: 1, py: 0.5, fontSize: '11px', fontWeight: 500, color: '#374151' }}>{meta.val || '-'}</Typography>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Box>

                                            {/* Budget Bar */}
                                            <Box sx={{ p: '8px 14px 10px', borderBottom: '1px solid #e8eaed', bgcolor: '#fff' }}>
                                                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                                                    <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>Budget allocation</Typography>
                                                    <Typography sx={{ fontSize: '10px', color: '#374151', fontWeight: 600 }}>{formatNumber(io.poAmount)} total</Typography>
                                                </Stack>
                                                <Box sx={{ height: 6, bgcolor: '#e8eaed', borderRadius: 10, overflow: 'hidden' }}>
                                                    <Box sx={{ height: '100%', width: `${budgetPct}%`, bgcolor: isOverBudget ? '#ef4444' : '#2563eb', transition: 'width 0.3s ease' }} />
                                                </Box>
                                                <Stack direction="row" justifyContent="space-between" mt={0.5}>
                                                    <Typography sx={{ fontSize: '10px', color: '#2563eb', fontWeight: 600 }}>{formatNumber(io.allocAmount || 0)} allocated</Typography>
                                                    <Typography sx={{ fontSize: '10px', color: isOverBudget ? '#ef4444' : '#6b7280', fontWeight: isOverBudget ? 600 : 400 }}>
                                                        {isOverBudget ? `${formatNumber((io.allocAmount || 0) - io.poAmount)} over budget` : `${formatNumber(io.poAmount - (io.allocAmount || 0))} remaining`}
                                                    </Typography>
                                                </Stack>
                                            </Box>

                                            {/* Placement Table */}
                                            <TableContainer sx={{ maxHeight: 350 }}>
                                                <Table size="small" stickyHeader sx={{ '& th': { fontSize: '10.5px', textTransform: 'uppercase', color: '#6b7280', bgcolor: '#f8f9fb', p: '9px 8px', whiteSpace: 'nowrap' }, '& td': { fontSize: '11.5px', p: '8px 8px', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f2f5' } }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell width={40}>
                                                                {!io.mode || io.mode === 'placement' ? (
                                                                    <Checkbox size="small" sx={{ p: 0 }}
                                                                        checked={selectablePlacements.length > 0 && io.placements.length === selectablePlacements.length}
                                                                        indeterminate={io.placements.length > 0 && io.placements.length < selectablePlacements.length}
                                                                        disabled={isIOLocked}
                                                                        onChange={(e) => toggleAllPlacements(io.poCode, e.target.checked)} />
                                                                ) : null}
                                                            </TableCell>
                                                            <TableCell>Placement ID</TableCell>
                                                            <TableCell>{ getDisplayName('site') }</TableCell>
                                                            <TableCell>Placement Name</TableCell>
                                                            <TableCell>Start Date</TableCell>
                                                            <TableCell>End Date</TableCell>
                                                            <TableCell>Cost Type</TableCell>
                                                            <TableCell align="right">Quantity</TableCell>
                                                            <TableCell align="right">Rate</TableCell>
                                                            <TableCell align="right">Amount</TableCell>
                                                            <TableCell align="right">Consumed Amt</TableCell>
                                                            <TableCell align="right">Remaining Amt</TableCell>
                                                            <TableCell align="right">Alloc. Amount</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {ioPlacementsList.map(p => {
                                                                            const isConsumed = (p.amount - (p.consumedAmt || 0)) <= 0;
                                                                            const isChecked = io.mode === 'io' || io.placements.some(sel => sel.placementId === p.placementId);
                                                                            const selData = io.placements.find(sel => sel.placementId === p.placementId) || {};
                                                                            const otherAlloc = getOtherLinesAllocated(io.poCode, p.placementId);
                                                                            const plRem = (p.amount - (p.consumedAmt || 0)) - otherAlloc;

                                                                            return (
                                                                                <TableRow key={p.placementId} hover sx={{ bgcolor: isConsumed ? '#f3f4f6' : (isChecked ? '#f0f9ff' : 'inherit'), '& td': { color: isConsumed ? '#9ca3af' : '#374151' } }}>
                                                                    <TableCell>
                                                                        <Checkbox size="small" sx={{ p: 0 }} 
                                                                            checked={isChecked} 
                                                                            disabled={isIOLocked || io.mode === 'io' || isConsumed} 
                                                                            onChange={(e) => togglePlacement(io.poCode, p, e.target.checked)} />
                                                                    </TableCell>
                                                                    <TableCell sx={{ color: '#2563eb !important', fontWeight: 500 }}>{p.placementId || p.placementCode}</TableCell>
                                                                    <TableCell>{p.siteName || ''}</TableCell>
                                                                    <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }} title={p.placementName}>{p.placementName}</TableCell>
                                                                    <TableCell sx={{ color: '#6b7280 !important' }}>{fDate(p.startDate)}</TableCell>
                                                                    <TableCell sx={{ color: '#6b7280 !important' }}>{fDate(p.endDate)}</TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{ display: 'inline-block', borderRadius: '4px', px: '7px', py: '1px', fontSize: '10px', fontWeight: 500, border: '1px solid', ...((p.costMethod || p.costType) === 'CPM' ? { bgcolor: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0' } : { bgcolor: '#fffbeb', color: '#92400e', borderColor: '#fcd34d' }) }}>
                                                                            {p.costMethod || p.costType || 'Flat'}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell align="right">{p.quantity?.toLocaleString()}</TableCell>
                                                                    <TableCell align="right">{formatNumber(p.rate)}</TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 500 }}>{formatNumber(p.amount)}</TableCell>
                                                                    <TableCell align="right" sx={{ color: !p.consumedAmt ? '#9ca3af !important' : 'inherit' }}>{formatNumber(p.consumedAmt || 0)}</TableCell>
                                                                    <TableCell align="right" sx={{ color: plRem > 0 ? '#047857 !important' : '#9ca3af !important', fontWeight: plRem > 0 ? 500 : 400 }}>{formatNumber(plRem)}</TableCell>
                                                                    <TableCell align="right">
                                                                        <TextField 
                                                                            size="small" 
                                                                            variant="outlined" 
                                                                            disabled={!isChecked || io.mode === 'io' || isIOLocked || (io.mode === 'placement' && info.disallowPartialPlacementConsumption === 1)} 
                                                                            value={isChecked ? (selData.allocAmount !== undefined ? selData.allocAmount : plRem) : ''}
                                                                            onChange={(e) => handleallocAmountChange(io.poCode, p.placementId, e.target.value)}
                                                                            placeholder="0.00"
                                                                            sx={{ 
                                                                                width: 100, 
                                                                                '& .MuiOutlinedInput-root': { 
                                                                                    bgcolor: (!isChecked || io.mode === 'io' || isIOLocked || (io.mode === 'placement' && info.disallowPartialPlacementConsumption === 1)) ? '#f3f4f6' : '#fff',
                                                                                    '& fieldset': { borderColor: (!isChecked || io.mode === 'io' || isIOLocked || (io.mode === 'placement' && info.disallowPartialPlacementConsumption === 1)) ? 'transparent' : '#ced4da' } 
                                                                                },
                                                                                '& input': { py: 0.5, px: 1, fontSize: '11px', textAlign: 'right', color: '#111827' }
                                                                            }}
                                                                        />
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            
                                            {/* Block Footer */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '8px 14px', borderTop: '1px solid #f0f2f5', bgcolor: '#f8f9fb' }}>
                                                <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                                                     {io.mode === 'io' ? `Invoicing entire IO (${ioPlacementsList.length} placements)` : `${checkedCount} of ${selectablePlacements.length} placements selected`}
                                                </Typography>
                                                <Stack direction="row" alignItems="center" gap={1}>
                                                    <Typography sx={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>Subtotal allocated</Typography>
                                                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#111827', minWidth: 100, textAlign: 'right' }}>{formatNumber(io.allocAmount || 0)}</Typography>
                                                </Stack>
                                            </Box>
                                        </Paper>
                                    );
                                })}
                            </Box>

                            {/* Grand Total Bar */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, p: '10px 16px', borderTop: '2px solid #e8eaed', bgcolor: '#fff', flexShrink: 0 }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#6b7280' }}>Grand total allocated</Typography>
                                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e', minWidth: 110, textAlign: 'right' }}>{formatNumber(grandTotalAllocated)}</Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: '11px 22px', bgcolor: '#fafafa', borderTop: '1px solid #e8eaed' }}>
                <Button onClick={() => onClose(null)} variant="outlined" sx={{ fontSize: '12px', fontWeight: 500, textTransform: 'none', px: 3 }}>Cancel</Button>
                <Tooltip title={getTooltipMessage()} placement="top">
                    <span>
                        <Button onClick={() => onClose(null, selectedIOs, sessionMode)} variant="contained" color="primary" disabled={isValidationFailed} sx={{ fontSize: '12px', fontWeight: 500, textTransform: 'none', px: 3 }}>Select</Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
        <GenericMessageDialog
            type={'confirm'}
            open={openConfirm}
            onClose={() => { }}
            message={"Are you sure, you want to uncheck 'Include All IO/PO'? Selected IOs outside the invoice's service period will be removed."}
            acceptCallback={confirmDisableIncludeAllPO}
            rejectCallback={cancelDisableIncludeAllPO}
            maxWidthPx={400}
        />
        </>
    );
}
